import { useState, useEffect } from "react"
import { Box, CircularProgress, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { useToast } from "@crm-src/hooks/use-toast"
import { invoicesApi } from "@crm-src/lib/invoices-api"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { AlertTriangle } from "lucide-react"
import { formatCurrency } from "@crm-src/lib/utils"
import { formatDate } from "@crm-src/lib/date-utils"

interface Invoice {
  /** A quote mirrored from a reseller's portal request (2026-09-15): deleting
   * it removes the reseller's own row too, so the dialog must say so. */
  locked?: boolean
  bill_to_reseller?: { id: number; name: string; code?: string } | null
  id: string
  invoice_number: string
  total_amount: number
  issue_date: string
  due_date: string
  paid_date?: string
  payment_status: string
}

interface DeleteInvoiceModalProps {
  isOpen: boolean
  onClose: () => void
  invoiceId: string | null
  onConfirmDelete?: (invoiceId: string) => void | Promise<void>
  documentType?: "invoice" | "quote"
}

const PAYMENT_STATUS_COLOR: Record<string, string> = {
  paid: "success.main",
  overdue: "error.main",
}

export function DeleteInvoiceModal({
  isOpen,
  onClose,
  invoiceId,
  onConfirmDelete,
  documentType = "invoice",
}: DeleteInvoiceModalProps) {
  const { toast } = useToast()
  const isQuote = documentType === "quote"
  const noun = isQuote ? "quote" : "invoice"
  const Noun = isQuote ? "Quote" : "Invoice"

  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadedFor, setLoadedFor] = useState<string | null>(null)

  // Fetch invoice details when modal opens for a new id
  useEffect(() => {
    if (!isOpen || !invoiceId) {
      setInvoice(null)
      setLoadedFor(null)
      return
    }
    if (loadedFor === invoiceId) return

    let cancelled = false
    setIsLoading(true)
    ;(async () => {
      try {
        const response = await invoicesApi.getById(invoiceId)
        if (cancelled) return
        if (response.success && response.data?.data) {
          setInvoice(response.data.data)
          setLoadedFor(invoiceId)
        } else {
          toast({ title: "Error", description: `Failed to load ${noun} details`, variant: "destructive" })
          onClose()
        }
      } catch (error: any) {
        if (cancelled) return
        if (error?.response?.status === 404) {
          console.warn(`${Noun} ${invoiceId} not found - likely already deleted`)
        } else {
          toast({ title: "Error", description: `Failed to load ${noun} details`, variant: "destructive" })
        }
        onClose()
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOpen, invoiceId, loadedFor, noun, Noun, toast, onClose])

  const handleDelete = async () => {
    if (!invoice || !onConfirmDelete) return
    await onConfirmDelete(invoice.id)
    setInvoice(null)
    setLoadedFor(null)
  }

  if (!isOpen || !invoiceId) return null

  const statusColor = PAYMENT_STATUS_COLOR[invoice?.payment_status ?? ""] ?? "warning.main"

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Delete ${Noun}`}
      // The body below already described the soft delete correctly; this header
      // contradicted it. `invoices#destroy` calls `soft_delete!` and explicitly
      // leaves the files alone.
      description={
        // A reseller's request leaves BOTH lists: crm-api deletes the reseller's
        // own row with the CRM copy (2026-09-15).
        invoice?.locked && invoice.bill_to_reseller
          ? `This ${noun} was requested by ${invoice.bill_to_reseller.name} on XOP Anytime. Deleting it here removes it from their portal too.`
          : `This marks the ${noun} as deleted — it can be restored if needed.`
      }
      confirmLabel="Delete"
      onConfirm={handleDelete}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} sx={{ color: "text.disabled" }} />
          <Typography component="span" variant="body2" sx={{ ml: 1, color: "text.secondary" }}>
            Loading {noun} details...
          </Typography>
        </Box>
      ) : invoice ? (
        <>
          <Box sx={{ bgcolor: "surfaceSoft", borderRadius: 1, p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="span" variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
                {Noun} Number
              </Typography>
              <Typography component="span" variant="body2" sx={{ color: "text.primary", fontFamily: "monospace" }}>
                {invoice.invoice_number}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="span" variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
                Amount
              </Typography>
              <Typography component="span" variant="body2" sx={{ color: "text.primary", fontWeight: 600 }}>
                {formatCurrency(invoice.total_amount)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="span" variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
                Issue Date
              </Typography>
              <Typography component="span" variant="body2" sx={{ color: "text.primary" }}>
                {formatDate(invoice.issue_date)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography component="span" variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
                Status
              </Typography>
              <Typography component="span" variant="body2" sx={{ fontWeight: 500, textTransform: "capitalize", color: statusColor }}>
                {invoice.payment_status}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ bgcolor: (t) => alpha(t.palette.error.main, 0.12), border: 1, borderColor: (t) => alpha(t.palette.error.main, 0.3), borderRadius: 1, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box sx={{ color: "error.main", mt: 0.25, flexShrink: 0, display: "flex" }}>
                <AlertTriangle size={20} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: "error.dark", mb: 0.5 }}>
                  Are you sure you want to delete this {noun}?
                </Typography>
                <Typography variant="body2" sx={{ color: "error.dark" }}>
                  This will mark the {noun} as deleted. It can be restored if needed. Associated files will be preserved.
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      ) : null}
    </ConfirmDialog>
  )
}
